import { createStyles } from "@mantine/core";
import { LuX, LuStethoscope, LuBuilding2 } from "react-icons/lu";

const useStyles = createStyles(() => ({
  chooserOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  chooser: {
    background: "var(--app-surface)",
    borderRadius: "14px",
    padding: "22px 22px 20px",
    maxWidth: "520px",
    width: "calc(100vw - 32px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    position: "relative",
  },

  chooserHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "0px",
  },

  chooserTitle: {
    fontFamily: "Inter, sans-serif",
    fontSize: "17px",
    fontWeight: 700,
    color: "var(--app-text)",
    lineHeight: "1.3",
    letterSpacing: "-0.012em",
  },

  chooserClose: {
    display: "grid",
    placeItems: "center",
    width: "26px",
    height: "26px",
    background: "transparent",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    color: "var(--app-text-muted)",
    fontSize: "18px",
    lineHeight: 1,
    flexShrink: 0,
    "&:hover": { background: "var(--app-border)", color: "var(--app-text)" },
    "& svg": { width: "16px", height: "16px", display: "block" },
  },

  chooserSub: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
    fontWeight: 400,
    color: "var(--app-text-muted)",
    margin: "4px 0 16px",
  },

  chooserOptions: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  claimChoice: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    padding: "14px 16px",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    cursor: "pointer",
    background: "var(--app-surface)",
    width: "100%",
    textAlign: "left",
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": {
      borderColor: "var(--app-primary)",
      background: "var(--app-tint-amber)",
    },
  },

  claimChoiceDisabled: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    padding: "14px 16px",
    border: "1px solid var(--app-border)",
    borderRadius: "12px",
    cursor: "not-allowed",
    background: "var(--app-surface-2)",
    width: "100%",
    textAlign: "left",
    opacity: 0.6,
  },

  claimChoiceIco: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "var(--app-tint-amber)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { width: "20px", height: "20px", color: "#B7791F", display: "block" },
  },

  claimChoiceIcoDisabled: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    background: "var(--app-surface-2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { width: "20px", height: "20px", color: "var(--app-text-muted)", display: "block" },
  },

  claimChoiceInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },

  claimChoiceName: {
    margin: 0,
    fontFamily: "Inter, sans-serif",
    fontSize: "14.5px",
    fontWeight: 700,
    color: "var(--app-text)",
    lineHeight: "1.3",
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },

  claimChoiceDesc: {
    fontFamily: "Inter, sans-serif",
    fontSize: "12.5px",
    fontWeight: 400,
    color: "var(--app-text-muted)",
    lineHeight: "1.5",
    margin: 0,
  },

  typeCode: {
    display: "inline-block",
    fontFamily: "monospace",
    fontSize: "10px",
    fontWeight: 600,
    color: "var(--app-text)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: "6px",
    padding: "1px 5px",
    letterSpacing: "0.04em",
    marginLeft: "2px",
  },

  soonBadge: {
    display: "inline-block",
    fontFamily: "Inter, sans-serif",
    fontSize: "9.5px",
    fontWeight: 600,
    color: "var(--app-text-muted)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: "999px",
    padding: "2px 7px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
}));

const ClaimTypeChooser = ({ open, onClose, onProfessional, onInstitutional }) => {
  const { classes } = useStyles();

  if (!open) return null;

  return (
    <div
      className={classes.chooserOverlay}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={classes.chooser}>
        <div className={classes.chooserHead}>
          <span className={classes.chooserTitle}>Start Claim</span>
          <button className={classes.chooserClose} onClick={onClose}>
            <LuX />
          </button>
        </div>
        <p className={classes.chooserSub}>Choose Claim Type</p>

        <div className={classes.chooserOptions}>
          <button
            className={classes.claimChoice}
            onClick={() => { onClose(); onProfessional(); }}
          >
            <div className={classes.claimChoiceIco}>
              <LuStethoscope />
            </div>
            <div className={classes.claimChoiceInfo}>
              <h4 className={classes.claimChoiceName}>
                Professional Claim
                <span className={classes.typeCode}>CMS-1500 · 837P</span>
              </h4>
              <p className={classes.claimChoiceDesc}>
                For outpatient services such as home health, therapy,
                behavioral health, and most office visits.
              </p>
            </div>
          </button>

          <div className={classes.claimChoiceDisabled}>
            <div className={classes.claimChoiceIcoDisabled}>
              <LuBuilding2 />
            </div>
            <div className={classes.claimChoiceInfo}>
              <h4 className={classes.claimChoiceName}>
                Institutional Claim
                <span className={classes.typeCode}>UB-04 · 837i</span>
                <span className={classes.soonBadge}>Coming soon</span>
              </h4>
              <p className={classes.claimChoiceDesc}>
                For facility services such as hospital, skilled nursing, and
                inpatient stays.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimTypeChooser;
